/**
 * Properties carried on each pipeline GeoJSON feature, as projected by the backend
 * `waterpipelines/md/{bbox}` endpoint. Only these three fields are available.
 */
export interface PipelineFeatureProperties {
    id: string;
    net_func: string | null;
    length: number | null;
}

// Friendly labels for the `net_func` values confirmed from the WaterPipelines API
// (uppercase strings). `net_func` also drives the pipeline line colour (see
// usePipelinesLayer), so these labels and that colour scheme cover the same value set.
const NET_FUNC_LABELS: Readonly<Record<string, string>> = {
    TRUNK: 'Trunk Main',
    DISTRIBUTION: 'Distribution Main',
    'COMMUNICATION PIPE': 'Communication Pipe',
    SUPPLY: 'Customer Supply Line',
    PRIVATE: 'Private Pipe',
    'DISCHARGE (ETS)': 'ETS Discharge Pipe',
};

/**
 * Returns a friendly display label for a pipeline's `net_func` type. Known values use
 * the curated label; any other non-empty value is title-cased so a type the API adds
 * later is still shown rather than dropped. Null/empty values return 'Unknown'.
 */
export function formatNetFunc(netFunc: string | null | undefined): string {
    if (!netFunc) return 'Unknown';
    const known = NET_FUNC_LABELS[netFunc];
    if (known) return known;
    return netFunc
        .toLowerCase()
        .split(' ')
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
}

/**
 * Formats a pipeline length (metres) for display, e.g. 1234.5 → "1,234.5 m". Rounds to
 * one decimal place and uses thousands separators. Returns null when the value is
 * absent or not a finite number, so callers can show a placeholder.
 */
export function formatPipeLength(length: number | null | undefined): string | null {
    if (length == null || !Number.isFinite(length)) return null;
    const rounded = Math.round(length * 10) / 10;
    return `${rounded.toLocaleString('en-GB')} m`;
}
